import type {
  OfferErrorContext,
  OrderProductModel,
  StoreShortType,
} from '@/types'

export interface OfferLine {
  productCode: number
  storeCode: number
  storeSupplierId: string | undefined
  route: string[]
  description: string
  article: string
  brandName?: string
  storeName: string
}

interface OfferLineNomenclature {
  code: number
  article: string
  description: string
  brandDto?: { description: string } | null
}

interface StockOfferLineSource {
  nomenclatureDto: OfferLineNomenclature
  suppliersStock: Array<{
    stock: Array<{ storeShortModel: StoreShortType; route?: string[] }>
  }>
}

type FlatOfferLineSource = Pick<
  OrderProductModel,
  'storeShortModel' | 'route'
> & { nomenclatureDto: OfferLineNomenclature }

const buildOfferLine = (
  nomenclature: OfferLineNomenclature,
  store: StoreShortType,
  route: string[]
): OfferLine => ({
  productCode: nomenclature.code,
  storeCode: store.code,
  storeSupplierId: store.supplierId,
  route,
  description: nomenclature.description,
  article: nomenclature.article,
  brandName: nomenclature.brandDto?.description,
  storeName: store.description,
})

export const toStockOfferLines = (products: StockOfferLineSource[]) =>
  products.flatMap((product) =>
    product.suppliersStock.flatMap((supplier) =>
      supplier.stock.map((stock) =>
        buildOfferLine(
          product.nomenclatureDto,
          stock.storeShortModel,
          stock.route ?? []
        )
      )
    )
  )

export const toFlatOfferLines = (products: FlatOfferLineSource[]) =>
  products.flatMap((product) =>
    product.storeShortModel
      ? [
          buildOfferLine(
            product.nomenclatureDto,
            product.storeShortModel,
            product.route
          ),
        ]
      : []
  )

const isSameRoute = (first: string[], second: string[]) =>
  first.length === second.length &&
  first.every((supplierId, index) => supplierId === second[index])

export const findOfferLine = (
  lines: OfferLine[],
  { key, route }: OfferErrorContext
): OfferLine | undefined => {
  const sameKeyLines = lines.filter(
    (line) =>
      line.productCode === key.productCode &&
      line.storeCode === key.storeCode &&
      line.storeSupplierId === key.supplierId
  )
  if (sameKeyLines.length <= 1) return sameKeyLines[0]
  return (
    sameKeyLines.find((line) => isSameRoute(line.route, route)) ??
    sameKeyLines[0]
  )
}
